import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Client Types:
 * - ADMIN: Full admin panel access (determined by user_roles table)
 * - TEST_LIFETIME: Mimics Lifetime access without payment (for testing)
 * - TEST_MONTHLY: Mimics Monthly access without payment (for testing)
 * - LIFETIME: Paid lifetime - full access to all content
 * - MONTHLY: Paid monthly - gradual unlock (1st module, then monthly or completion-based unlock)
 * - FREEMIUM: Registered but not paid - only Academy 1, Module 1, Class 1
 */
export type ClientType = 
  | 'admin' 
  | 'test_lifetime' 
  | 'test_monthly' 
  | 'lifetime' 
  | 'monthly' 
  | 'freemium';

export interface ClientAccessInfo {
  clientType: ClientType;
  clientId: string | null;
  plan: string | null;
  hasAccess: boolean;
  paid: boolean;
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  daysRemaining: number | null;
  isLifetimeAccess: boolean;
  isTestAccount: boolean;
}

interface CompletedModule {
  academyId: string;
  moduleNumber: number;
}

export interface ClientAccessResult {
  loading: boolean;
  clientInfo: ClientAccessInfo;
  
  // Access check methods
  canAccessAcademy: (academyId: string) => boolean;
  canAccessModule: (academyId: string, moduleNumber: number, completedModules?: CompletedModule[]) => boolean;
  canAccessClass: (academyId: string, moduleNumber: number, classNumber: number) => boolean;
  isContentFree: (academyId: string, moduleNumber: number, classNumber?: number) => boolean;
  
  // Helper flags
  showSubscriptionWall: boolean;
  showUpgradeBanner: boolean;
  
  // Refresh
  refreshClientInfo: () => Promise<void>;
}

// Free content: Academy 1, Module 1, Class 1
const FREE_ACADEMY_ID = 'faceless-content';
const FREE_MODULE_NUMBER = 1;
const FREE_CLASS_NUMBER = 1;

export const useClientAccess = (): ClientAccessResult => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState<ClientAccessInfo>({
    clientType: 'freemium',
    clientId: null,
    plan: null,
    hasAccess: false,
    paid: false,
    subscriptionStart: null,
    subscriptionEnd: null,
    daysRemaining: null,
    isLifetimeAccess: false,
    isTestAccount: false,
  });

  const fetchClientInfo = useCallback(async () => {
    if (!user) {
      setClientInfo({
        clientType: 'freemium',
        clientId: null,
        plan: null,
        hasAccess: false,
        paid: false,
        subscriptionStart: null,
        subscriptionEnd: null,
        daysRemaining: null,
        isLifetimeAccess: false,
        isTestAccount: false,
      });
      setLoading(false);
      return;
    }

    // Admin check first
    if (isAdmin) {
      setClientInfo({
        clientType: 'admin',
        clientId: null,
        plan: 'Admin',
        hasAccess: true,
        paid: true,
        subscriptionStart: null,
        subscriptionEnd: null,
        daysRemaining: null,
        isLifetimeAccess: true,
        isTestAccount: false,
      });
      setLoading(false);
      return;
    }

    // Always check subscription status with Stripe on every login/load
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.access_token) {
        const { error: syncError } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
        });
        if (syncError) {
          console.warn('Subscription sync warning:', syncError.message);
        }
      }
    } catch (syncErr) {
      console.warn('Failed to sync subscription status:', syncErr);
    }

    try {
      const { data: client } = await supabase
        .from('clients')
        .select('id, plan, has_access, paid, subscription_start, subscription_end')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!client) {
        // No client record - freemium user
        setClientInfo({
          clientType: 'freemium',
          clientId: null,
          plan: null,
          hasAccess: false,
          paid: false,
          subscriptionStart: null,
          subscriptionEnd: null,
          daysRemaining: null,
          isLifetimeAccess: false,
          isTestAccount: false,
        });
        setLoading(false);
        return;
      }

      // Determine client type based on plan and payment status
      let clientType: ClientType = 'freemium';
      let isLifetimeAccess = false;
      let isTestAccount = false;
      let hasAccess = client.has_access === true;

      const plan = client.plan?.toLowerCase() || '';

      // Check for test accounts first
      if (plan === 'test_lifetime' || plan === 'test-lifetime') {
        clientType = 'test_lifetime';
        isLifetimeAccess = true;
        isTestAccount = true;
        hasAccess = true;
      } else if (plan === 'test_monthly' || plan === 'test-monthly') {
        clientType = 'test_monthly';
        isTestAccount = true;
        hasAccess = true;
      } else if (plan === 'lifetime') {
        if (client.paid && client.has_access) {
          clientType = 'lifetime';
          isLifetimeAccess = true;
        } else {
          clientType = 'freemium';
        }
      } else if (plan === 'monthly') {
        if (client.paid && client.has_access) {
          clientType = 'monthly';
        } else {
          clientType = 'freemium';
        }
      } else if (plan === 'freemium') {
        // Explicit freemium plan - they get limited access to free content
        clientType = 'freemium';
        hasAccess = true; // They have access to freemium content
      } else {
        // Unknown plan or no plan - treat as freemium unless has access
        if (client.paid && client.has_access) {
          clientType = 'monthly'; // Default to monthly if paid but unknown plan
        } else {
          clientType = 'freemium';
        }
      }

      // Calculate days remaining for non-lifetime
      let daysRemaining: number | null = null;
      if (client.subscription_end && !isLifetimeAccess) {
        const endDate = new Date(client.subscription_end);
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }

      setClientInfo({
        clientType,
        clientId: client.id,
        plan: client.plan,
        hasAccess,
        paid: client.paid,
        subscriptionStart: client.subscription_start,
        subscriptionEnd: client.subscription_end,
        daysRemaining,
        isLifetimeAccess,
        isTestAccount,
      });
    } catch (error) {
      console.error('Error fetching client info:', error);
      setClientInfo({
        clientType: 'freemium',
        clientId: null,
        plan: null,
        hasAccess: false,
        paid: false,
        subscriptionStart: null,
        subscriptionEnd: null,
        daysRemaining: null,
        isLifetimeAccess: false,
        isTestAccount: false,
      });
    }

    setLoading(false);
  }, [user, isAdmin]);

  useEffect(() => {
    if (!authLoading) {
      fetchClientInfo();
    }
  }, [authLoading, fetchClientInfo]);

  // Check if content is free (available to all)
  // FREEMIUM: Only Academy 1, Module 1, Class 1 is free - ONE class only!
  const isContentFree = useCallback((academyId: string, moduleNumber: number, classNumber?: number): boolean => {
    // Only Academy 1, Module 1, Class 1 is free
    if (academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER) {
      // If checking a specific class, ONLY class 1 is free
      if (classNumber !== undefined) {
        return classNumber === FREE_CLASS_NUMBER;
      }
      // Module page access is allowed for freemium to see locked classes
      return true;
    }
    return false;
  }, []);

  // Can access academy
  const canAccessAcademy = useCallback((academyId: string): boolean => {
    const { clientType } = clientInfo;

    // Admin, Lifetime, Test_Lifetime have full access
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') {
      return true;
    }

    // Monthly and Test_Monthly have access to all academies (modules are controlled)
    if (clientType === 'monthly' || clientType === 'test_monthly') {
      return true;
    }

    // Freemium can only access the first academy
    if (clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID;
    }

    return false;
  }, [clientInfo]);

  // Can access module
  // For monthly users: completion-based unlock - must complete 100% of previous module in the SAME academy
  const canAccessModule = useCallback((academyId: string, moduleNumber: number, completedModules?: CompletedModule[]): boolean => {
    const { clientType } = clientInfo;

    // Admin, Lifetime, Test_Lifetime have full access
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') {
      return true;
    }

    // Monthly and Test_Monthly: completion-based gradual unlock (strict sequential)
    if (clientType === 'monthly' || clientType === 'test_monthly') {
      // First module of any academy is always unlocked for monthly subscribers
      if (moduleNumber === 1) {
        return true;
      }

      // Check if ALL previous modules in the SAME academy are completed (strict sequential)
      if (completedModules && completedModules.length > 0) {
        // User can access module N only if modules 1 through N-1 are ALL completed
        for (let i = 1; i < moduleNumber; i++) {
          const isCompleted = completedModules.some(
            m => m.academyId === academyId && m.moduleNumber === i
          );
          if (!isCompleted) {
            return false;
          }
        }
        return true;
      }

      // If no completion data provided, only first module is accessible
      return false;
    }

    // Freemium can only access the first module of first academy
    if (clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER;
    }

    return false;
  }, [clientInfo]);

  // Can access class
  // FREEMIUM: ONLY Class 1 of Module 1 of Academy 1 - nothing else!
  const canAccessClass = useCallback((academyId: string, moduleNumber: number, classNumber: number): boolean => {
    const { clientType } = clientInfo;

    // Admin, Lifetime, Test_Lifetime have full access
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') {
      return true;
    }

    // Monthly and Test_Monthly: if module is accessible, all classes in it are accessible
    if (clientType === 'monthly' || clientType === 'test_monthly') {
      return canAccessModule(academyId, moduleNumber);
    }

    // Freemium can ONLY access Class 1 of Module 1 of Academy 1 - strictly one class!
    if (clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER && classNumber === FREE_CLASS_NUMBER;
    }

    return false;
  }, [clientInfo, canAccessModule]);

  // Show subscription wall ONLY for users with no client record at all
  // Freemium users should see the dashboard with limited access, not a wall
  const showSubscriptionWall = useMemo(() => {
    // Only show wall if there's no client record (null clientId and not freemium plan)
    // Freemium users with a client record should see the dashboard
    if (clientInfo.clientType === 'freemium' && clientInfo.clientId) {
      return false; // They have a freemium record, let them in
    }
    // Show wall for users without any client record
    return clientInfo.clientType === 'freemium' && !clientInfo.clientId && !clientInfo.hasAccess;
  }, [clientInfo]);

  // Show upgrade banner for monthly users nearing expiration
  const showUpgradeBanner = useMemo(() => {
    const { clientType, daysRemaining } = clientInfo;
    if (clientType === 'monthly' && daysRemaining !== null && daysRemaining <= 7) {
      return true;
    }
    return false;
  }, [clientInfo]);

  return {
    loading: loading || authLoading,
    clientInfo,
    canAccessAcademy,
    canAccessModule,
    canAccessClass,
    isContentFree,
    showSubscriptionWall,
    showUpgradeBanner,
    refreshClientInfo: fetchClientInfo,
  };
};
